import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, User, Users, Mail, Phone } from "lucide-react";

const GolfLessons = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const packages = [
    {
      label: "Single Lesson",
      price: "$70",
      desc: "One 45-minute private session with Chris Hill.",
      features: ["45 minutes of instruction", "Driving range & short game areas", "On-course time (subject to availability)"],
      featured: false,
    },
    {
      label: "3-Pack",
      price: "$200",
      desc: "Three lessons — great for working on specific areas of your game.",
      features: ["3 × 45-minute sessions", "Driving range & short game areas", "On-course time (subject to availability)", "Save $10 vs single rate"],
      featured: true,
    },
    {
      label: "5-Pack",
      price: "$300",
      desc: "Five lessons — the best value for committed improvement.",
      features: ["5 × 45-minute sessions", "Driving range & short game areas", "On-course time (subject to availability)", "Save $50 vs single rate"],
      featured: false,
    },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[450px] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1760926629591-40c9227c46cd?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85)" }}
        >
          <div className="absolute inset-0 bg-[#1B2A4A]/75" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">Professional Instruction</span>
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mt-3">2026 Golf Lessons</h1>
          <p className="text-white/70 text-lg mt-4 max-w-2xl">
            Private lessons with certified CPGA Golf Professional Chris Hill — on the range, short game area, and course.
          </p>
        </div>
      </section>

      {/* Instructor */}
      <section className="py-16 bg-[#f8f7f4]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row items-center gap-8 bg-white rounded-2xl p-8 shadow-sm border border-gray-100 max-w-3xl mx-auto">
            <div className="w-16 h-16 rounded-full bg-[#1B2A4A] flex items-center justify-center shrink-0">
              <User size={28} className="text-[#C9A96E]" />
            </div>
            <div className="text-center md:text-left">
              <div className="text-xs font-bold text-[#C9A96E] uppercase tracking-widest mb-1">Your Instructor</div>
              <h2 className="text-2xl font-serif font-bold text-[#1B2A4A]">Chris Hill</h2>
              <p className="text-gray-500 text-sm mt-1">Certified CPGA Golf Professional · Willow Ridge Golf & Country Club</p>
              <div className="flex flex-wrap justify-center md:justify-start gap-4 mt-3">
                <a href="tel:5196768772" className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#C9A96E] transition-colors">
                  <Phone size={13} /> (519) 676-8772
                </a>
                <a href="mailto:chill@willowridgegolf.ca" className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#C9A96E] transition-colors">
                  <Mail size={13} /> chill@willowridgegolf.ca
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lesson Details */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">Private Lessons</span>
            <h2 className="text-4xl font-serif font-bold text-[#1B2A4A] mt-3 mb-3">Lesson Packages</h2>
            <p className="text-gray-500 text-sm max-w-lg mx-auto">
              All lessons are 45 minutes, taught on the driving range and/or any short game area. On-course time available subject to availability.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {packages.map((pkg, i) => (
              <div key={i} className={`rounded-2xl overflow-hidden flex flex-col shadow-sm ${pkg.featured ? "border-2 border-[#C9A96E]" : "border border-gray-100"}`}>
                {pkg.featured && (
                  <div className="bg-[#C9A96E] text-[#1B2A4A] text-xs font-bold text-center py-1.5 uppercase tracking-wide">Best Value</div>
                )}
                <div className={`px-7 py-6 ${pkg.featured ? "bg-[#1B2A4A]" : "bg-[#f8f7f4]"}`}>
                  <div className={`text-xs font-bold uppercase tracking-widest mb-2 ${pkg.featured ? "text-[#C9A96E]" : "text-gray-400"}`}>{pkg.label}</div>
                  <div className={`text-3xl font-bold ${pkg.featured ? "text-white" : "text-[#1B2A4A]"}`}>{pkg.price}</div>
                  <p className={`text-sm mt-2 ${pkg.featured ? "text-white/60" : "text-gray-500"}`}>{pkg.desc}</p>
                </div>
                <div className="bg-white px-7 py-5 flex-1">
                  <ul className="space-y-2">
                    {pkg.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-gray-600">
                        <Check size={13} className="text-[#C9A96E] mt-0.5 shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          {/* Group Lessons */}
          <div className="bg-[#f8f7f4] rounded-2xl p-7 border border-gray-100 max-w-3xl mx-auto">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#C9A96E]/10 flex items-center justify-center shrink-0">
                <Users size={18} className="text-[#C9A96E]" />
              </div>
              <div>
                <h3 className="font-serif font-bold text-[#1B2A4A] mb-1">Group Lessons</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Groups of <strong>3 or more players</strong> — add <strong>$20 per lesson</strong> to the individual lesson cost. A great way to learn with friends or family.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1B2A4A]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl font-serif font-bold text-white mb-4">Book a Lesson with Chris</h2>
          <p className="text-white/60 mb-8">Call or email to schedule your private lesson or group session.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:5196768772"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#C9A96E] text-[#1B2A4A] font-semibold rounded-md hover:bg-[#d4b97e] transition-colors"
            >
              Call (519) 676-8772 <ArrowRight size={18} />
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white font-semibold rounded-md hover:bg-white/10 transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GolfLessons;
